import * as React from 'react';
import { BaseRouteProps } from '../../interfaces';
import { PVAStyle, LoadingIndicator, PVAPage } from '../../components';
import { SurveyVersion, Style } from '../../models';
import { StyleDataService, ReportDataService } from '../../services';
import { FilterStore, UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { exportToPDF } from '../../utilities/Pdf';
import { PdfStyle } from './pdf_style';
import './PVAStylesView.css';


interface PVAStylesState {
    orgId: number;
    surveyId: number;
    surveyVersionId: number;
    surveyVersion: SurveyVersion | undefined;
    styles: Style[];
    generating: boolean;
}

interface PVAStylesViewProps extends BaseRouteProps<{}> {
    FilterStore?: FilterStore;
    UserStore?: UserStore;
}

@inject('FilterStore', 'UserStore') @observer
export class PVAStylesView extends React.Component<PVAStylesViewProps, PVAStylesState> {
    private styleDataService = new StyleDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);

    constructor(props: PVAStylesViewProps) {
        super(props);

        this.state = {
            orgId: 0,
            surveyVersionId: 0,
            surveyId: 0,
            surveyVersion: undefined,
            styles: [],
            generating: false
        };
    }

    get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }
    
    get styles() {
        return this.state.styles;
    }

    public Update() {
        if (this.surveyVersion && (this.surveyVersion.id !== this.state.surveyVersionId || (this.styles.length === 0 && !this.state.generating))) {
            // pull down pva styles
            this.setState({
                surveyVersionId: this.surveyVersion.id
            });
            
            this.styleDataService.getAllBySurveyVersionId(this.surveyVersion!.id).then( (styles: Style[]) => {
                this.setState({
                    styles: styles
                });
            });
        }
    }

    public componentDidMount() {
        this.Update();
    }

    componentWillReact() {
        this.Update();
    }

    private GetTextType(style: Style, languageId: Number, textType: String) {
        if (!style.styleTexts || style.styleTexts.length === 0) {
            return '';
        }

        var text = style.styleTexts.filter((x => x.languageId === languageId && x.styleTextType === textType));
        if (text && text.length > 0) {
            return text[0].value;
        }
        return '';
    }
    
    private exportPDF_fromServer(id: number, name: string) {
        this.setState(
            {
                generating: true
            },
            () => {
                this.reportDataService.getStylePDF(id, name).then(() => {
                    this.setState({
                        generating: false
                    });
                }).catch(() => {
                    this.setState({
                        generating: false
                    });
                });
            }
        );
    }

    private exportPDF(id: number, name: string) {
        const style = this.styles.find(s => s.id === id);
        if (!style) {
            return;
        }

        this.setState(
            {
                generating: true
            },
            () => {
                const user = this.props.UserStore && this.props.UserStore.userInfo;
                const userName = user ? `${user.firstName} ${user.lastName}` : 'User';
                const organizationName = (user && user.organization && user.organization.name) || 'Organization';
                const date = new Date().toLocaleDateString();

                const selectedUserData = {
                    name: style.name,
                    style: {
                        ...style,
                        brief: this.GetTextType(style, 1, 'brief'),
                        content: this.GetTextType(style, 1, 'content'),
                        core: style.core
                    }
                };

                try {
                    exportToPDF(
                        <PdfStyle
                            userName={userName}
                            date={date}
                            organization={organizationName}
                            selectedUserData={selectedUserData}
                            surveyVersion={this.surveyVersion}
                            allStyles={this.styles}
                            notificationService={this.props.notificationService}
                            history={this.props.history}
                            location={this.props.location}
                            match={this.props.match}
                        />,
                        `${name}_Style.pdf`
                    );
                } catch (error) {
                    console.error('Error generating PDF:', error);
                    this.props.notificationService.addError('Error generating PDF.');
                } finally {
                    this.setState({
                        generating: false
                    });
                }
            }
        );
    }


    public render() {
        if (this.state.generating) {
            return <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true} />;
        }

        var stylesToList: Style[] = [];
        var stylesAdded = {};
        this.styles.map((s: Style) => {
            if (stylesAdded[s.name] === undefined) {
                stylesAdded[s.name] = s.name;
                stylesToList.push(s);
            }
        });

        let groups = [{
                header: "Export PDF",
                items: stylesToList.map(s => ({
                    id: s.id,
                    name: s.name,
                    callback: () => this.exportPDF(s.id, s.name)
                }))
            }];

        return (
            <PVAPage>
                <PVAPage.Header actions={groups}>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            The 8 PVA Styles
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                </PVAPage.Header>

                {(!this.styles || this.styles.length < 1) && <LoadingIndicator />}
                
                <PVAPage.Body>
                    <div className="pva-styles-list">
                        {stylesToList.map((s: Style) => {
                            return <PVAStyle 
                                        notificationService={this.props.notificationService} 
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}
                                        name={' '} 
                                        key={s.id} 
                                        style={s} 
                                        brief={this.GetTextType(s, 1, 'brief')} 
                                        content={this.GetTextType(s, 1, 'content')} 
                                        useAdditionalButtonsInsteadOfViewAll={true} 
                                        surveyVersion={this.surveyVersion}
                                        allStyles={this.styles}
                                        hideDichotomies={true}
                                    />;
                        })}
                    </div>
                </PVAPage.Body>
                {!this.state.generating && !!this.styles && this.styles.length > 0 && <PVAPage.Footer />}
            </PVAPage>);
    }
}


// WEBPACK FOOTER //
// ./src/components/pvastyles/PVAStylesView.tsx